import { QueryClient, useQuery } from '@tanstack/react-query'

import {
  PositionBalance,
  PositionBalanceResponse,
} from '~/components/smart-accounts/positions/types'
import { QUERY_KEYS } from '~/constants/react-query'
import { REFETCH_POSITIONS } from '~/constants/refetch-intervals'
import { useActiveOrgId } from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'

export const usePositionBalances = (
  smartAccountId?: string,
  orgId?: string,
) => {
  const { serverGetRequest } = useServerRequests()
  const activeOrgId = useActiveOrgId()
  const effectiveOrgId = orgId ?? activeOrgId
  const shouldFetch = !!smartAccountId && !!effectiveOrgId

  return useQuery<PositionBalance[]>({
    queryKey: [
      QUERY_KEYS.POSITION_ORG_ACCOUNT_BALANCES,
      effectiveOrgId,
      smartAccountId,
    ],
    enabled: shouldFetch,
    refetchInterval: REFETCH_POSITIONS,
    queryFn: async () => {
      if (!smartAccountId || !effectiveOrgId) {
        throw new Error('Smart account ID and org ID are required')
      }

      const response = await serverGetRequest<PositionBalanceResponse[]>({
        path: `/api/positions/balances/${effectiveOrgId}`,
        params: {
          accountId: smartAccountId,
        },
      })

      if (!response) {
        throw new Error('Failed to fetch position balances')
      }

      return response.data.map((balance) => ({
        ...balance,
        tokens: balance.tokens.map((token) => ({
          ...token,
          positionAmount: token.positionAmount
            ? BigInt(token.positionAmount)
            : null,
          claimableAmount: BigInt(token.claimableAmount),
        })),
      }))
    },
  })
}

export const invalidatePositionBalancesQuery = (
  queryClient: QueryClient,
  activeOrgId?: string,
  smartAccountId?: string,
) => {
  if (!smartAccountId) {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.POSITION_ORG_ACCOUNT_BALANCES, activeOrgId],
      exact: false,
    })
    return
  }

  queryClient.invalidateQueries({
    queryKey: [
      QUERY_KEYS.POSITION_ORG_ACCOUNT_BALANCES,
      activeOrgId,
      smartAccountId,
    ],
  })
}
